/**
 * Property API Service - handles all property data operations
 * This connects the admin dashboard to your MongoDB backend
 */

import AuthService from './authService';

const BASE_URL = 'https://back-end-x7tx.onrender.com/api';

/**
 * Generic request handler with auth — all authenticated requests flow through here.
 * AuthService.fetchWithAuth handles 401s globally (auto-logout + redirect to /).
 */
const makeRequest = async (endpoint, method = 'GET', data = null, options = {}) => {
  try {
    const headers = {};

    if (options.source) {
      headers['x-request-source'] = options.source;
    } else {
      headers['x-request-source'] = 'admin-dash';
    }

    const config = { method, headers };

    if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
      config.body = JSON.stringify(data);
    }

    const response = await AuthService.fetchWithAuth(`${BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'API request failed');
    }

    return await response.json();
  } catch (error) {
    console.error(`API error (${endpoint}):`, error);
    throw error;
  }
};

const PropertyService = {
  /**
   * Get all properties with optional filtering
   */
  getProperties: async (filters = {}, source = 'admin-dash') => {
    const queryParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    const endpoint = `/properties${queryString ? `?${queryString}` : ''}`;
    return await makeRequest(endpoint, 'GET', null, { source });
  },

  /**
   * Get a single property by ID
   */
  getPropertyById: async (id) => {
    return await makeRequest(`/properties/${id}`);
  },

  /**
   * Create a new property
   */
  createProperty: async (propertyData) => {
    return await makeRequest('/properties', 'POST', propertyData);
  },

  /**
   * Update an existing property
   */
  updateProperty: async (id, propertyData) => {
    return await makeRequest(`/properties/${id}`, 'PUT', propertyData);
  },

  /**
   * Delete a property
   */
  deleteProperty: async (id) => {
    return await makeRequest(`/properties/${id}`, 'DELETE');
  },

  /**
   * Get property count
   */
  getPropertyCount: async () => {
    return await makeRequest('/properties/count');
  },

  /**
   * Get agent property count
   */
  getAgentPropertyCount: async (agentId) => {
    return await makeRequest(`/properties/count?agent=${agentId}`);
  },

  /**
   * Get agent properties
   */
  getAgentProperties: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return await makeRequest(`/properties${queryString ? `?${queryString}` : ''}`, 'GET', null, { source: 'admin-dash' });
  },

  /**
   * Bulk import properties
   */
  bulkImportProperties: async (properties) => {
    return await makeRequest('/properties/bulk', 'POST', { properties });
  },

  /**
   * Update property images
   */
  updatePropertyImages: async (propertyId, imageUrls, mainImageUrl) => {
    return await makeRequest(`/properties/${propertyId}/images`, 'PUT', {
      images: imageUrls,
      mainImage: mainImageUrl
    });
  },

  /**
   * Pending properties
   */
  getPendingProperties: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return await makeRequest(`/pending-properties${queryString ? `?${queryString}` : ''}`);
  },

  getPendingPropertyCount: async () => {
    return await makeRequest('/pending-properties/count');
  },

  submitPropertyForReview: async (propertyData) => {
    return await makeRequest('/pending-properties', 'POST', propertyData);
  },

  approvePendingProperty: async (id, updateData) => {
    return await makeRequest(`/pending-properties/${id}/approve`, 'PUT', updateData);
  },

  rejectPendingProperty: async (id, feedbackData) => {
    return await makeRequest(`/pending-properties/${id}/reject`, 'PUT', feedbackData);
  },

  updatePendingProperty: async (id, propertyData) => {
    return await makeRequest(`/pending-properties/${id}`, 'PUT', propertyData);
  },

  /**
   * Neighborhoods
   */
  getNeighborhoods: async () => {
    return await makeRequest('/neighborhoods');
  },

  getNeighborhoodsPublic: async () => {
    // Public endpoint — no auth needed
    try {
      const response = await fetch(`${BASE_URL}/neighborhoods/public`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      if (!response.ok) {
        throw new Error(`Error fetching neighborhoods: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.error('Error in getNeighborhoodsPublic:', error);
      throw error;
    }
  },

  getNeighborhood: async (id) => {
    return await makeRequest(`/neighborhoods/${id}`);
  },

  createNeighborhood: async (data) => {
    return await makeRequest('/neighborhoods', 'POST', data);
  },

  updateNeighborhood: async (id, data) => {
    return await makeRequest(`/neighborhoods/${id}`, 'PUT', data);
  },

  deleteNeighborhood: async (id) => {
    return await makeRequest(`/neighborhoods/${id}`, 'DELETE');
  },

  updateNeighborhoodScores: async (id, scoreData) => {
    return await makeRequest(`/neighborhoods/${id}/scores`, 'PUT', scoreData);
  },

  updateNeighborhoodSchools: async (id, schoolData) => {
    return await makeRequest(`/neighborhoods/${id}/schools`, 'PUT', schoolData);
  },

  /**
   * Agents
   */
  getAgentRoster: async () => {
    return await makeRequest('/admin/agents');
  },

  /**
   * Upload image to Cloudinary — uses fetchWithAuth directly since FormData
   * can't go through makeRequest (no Content-Type: application/json)
   */
  uploadImage: async (imageFile) => {
    try {
      const formData = new FormData();
      formData.append('image', imageFile);

      const response = await AuthService.fetchWithAuth(`${BASE_URL}/uploads/cloudinary`, {
        method: 'POST',
        headers: {}, // let browser set Content-Type with boundary for FormData
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Image upload failed');
      }

      return await response.json();
    } catch (error) {
      console.error('Image upload error:', error);
      throw error;
    }
  },

  /**
   * MLS
   */
  getMLSRegions: async () => {
    return await makeRequest('/mls/regions');
  },

  setupMLSConnection: async (regionId, credentials) => {
    return await makeRequest('/mls/setup', 'POST', { regionId, ...credentials });
  },

  /**
   * Video generation
   *
   * Two endpoints powering the AI-video modal on AgentPropertyList:
   *
   *   GET  /api/videos/options         → getVideoOptions()
   *        Returns the server-driven config the modal needs to render:
   *        { limits: { minPhotos, maxPhotos, monthlyCap },
   *          formats: [{ id, label }],
   *          motionStyles: [{ id, label, description? }],
   *          musicTracks: [{ id, label }],
   *          addOns: [{ id, label, cost }],
   *          pricing: { base, perPhoto },
   *          agent: { pointsBalance, monthlyUsage } }
   *        Everything here is sourced from videoConfig.js on the backend
   *        so the frontend never holds the pricing/limits source of truth.
   *
   *   POST /api/videos/jobs            → createVideoJob(jobConfig)
   *        Body shape:
   *        { propertyId, photos: string[], formats: string[],
   *          motionStyle, musicTrack, addOns: string[] }
   *        Backend re-validates against videoConfig.js, deducts points
   *        from the agent's balance, and queues the render job. The job
   *        result itself arrives async (worker → webhook → notification).
   */
  getVideoOptions: async () => {
    return await makeRequest('/videos/options');
  },

  createVideoJob: async (jobConfig) => {
    return await makeRequest('/videos/jobs', 'POST', jobConfig);
  },

  /**
   * Agent's video library — paginated list of their video jobs.
   *
   * Backend (GET /api/videos/jobs) supports:
   *   - status:        filter by 'queued'|'generating'|'complete'|'failed'
   *   - page, limit:   pagination
   *   - includeHidden: include soft-deleted jobs (default false)
   *
   * Returns the controller's response shape — handle both array-direct
   * and { jobs, total, page, limit } at the call site.
   */
  listVideoJobs: async (params = {}) => {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, value);
      }
    });
    const queryString = queryParams.toString();
    return await makeRequest(`/videos/jobs${queryString ? `?${queryString}` : ''}`);
  },

  /**
   * Single video job by ID — used for detail views.
   */
  getVideoJob: async (id) => {
    return await makeRequest(`/videos/jobs/${id}`);
  },

  /**
   * Regenerate a completed job at 50% within the regen window.
   * Optional `options` override merges into the original job's options.
   */
  regenerateVideoJob: async (id, options = null) => {
    return await makeRequest(`/videos/jobs/${id}/regenerate`, 'POST', options ? { options } : {});
  },

  /**
   * Soft-delete a job from the agent's library. Preserves the record
   * for audit; just flips hiddenByAgent.
   */
  hideVideoJob: async (id) => {
    return await makeRequest(`/videos/jobs/${id}`, 'DELETE');
  },

  /**
   * Analytics
   */
  getTotalSwipes: async () => {
    return await makeRequest('/users/analytics/total-swipes');
  },

  getTotalClaimedUsers: async () => {
    return await makeRequest('/users/analytics/total-claimed');
  },

  getShowingCount: async () => {
    return await makeRequest('/showings/count');
  },

  /**
   * Listing boosts (admin)
   */
  getListingBoosts: async (propertyIds = []) => {
    const qs = propertyIds.length ? `?propertyIds=${propertyIds.join(',')}` : '';
    return await makeRequest(`/admin/listing-boosts${qs}`);
  },

  boostListing: async (propertyId, durationHours) => {
    return await makeRequest('/admin/listing-boosts', 'POST', { propertyId, durationHours });
  },

  endListingBoost: async (propertyId) => {
    return await makeRequest(`/admin/listing-boosts/property/${propertyId}`, 'DELETE');
  }
};

export default PropertyService;